import { ZonedDateTime } from '@internationalized/date';
import { CategorySortOption } from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export type CategoryAttribute = {
  name: string;
  description: string;
  dataType: string;
  required: boolean;
  validationExpr: string | null;
  options: string[] | null;
};

export type CategoryRecord = {
  id: string;
  name: string;
  description: string;
  attributes: CategoryAttribute[];
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export const categoriesListQueryAtom = createQueryAtomFactory(
  'settings.categoriesList',
  (params: {
    ids?: string[];
    searchTerm?: string;
    limit?: number;
    offset?: number;
    sorting?: CategorySortOption[];
  }) => ({
    categories: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        name: true,
        description: true,
        attributes: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { categories: CategoryRecord[]; totalCount: number } => {
      if (!data?.categories?.entries) {
        return { categories: [], totalCount: 0 };
      }

      return {
        categories: data.categories.entries.map((category) => {
          const rawAttributes = category.attributes as
            | Record<string, unknown>
            | undefined;
          const attributes: CategoryAttribute[] = rawAttributes
            ? Object.entries(rawAttributes).map(([name, value]) => {
                const attrValue = value as Record<string, unknown>;
                return {
                  name,
                  description: (attrValue?.description as string) ?? '',
                  dataType: (attrValue?.dataType as string) ?? 'STRING',
                  required: (attrValue?.required as boolean) ?? false,
                  validationExpr: (attrValue?.validationExpr as string) ?? null,
                  options: (attrValue?.options as string[]) ?? null,
                };
              })
            : [];
          return {
            id: category.id,
            name: category.name,
            description: category.description,
            attributes,
            timestamps: {
              createdAt: parseZonedDateTime(category.timestamps.createdAt),
              updatedAt: parseZonedDateTime(category.timestamps.updatedAt),
            },
          };
        }),
        totalCount: data.categories.page?.total ?? 0,
      };
    },
  },
);
